/**
 * Google Analytics 4 (SEO-05).
 *
 * Se carga SOLO si el build trae `VITE_GA_ID`. Sin el id no se pide ningun
 * script ni se deja ninguna cookie: en desarrollo no se mide el propio trabajo,
 * y un despliegue sin configurar no manda datos a la cuenta de nadie.
 *
 * El sitio es una SPA: el navegador carga una sola vez, asi que GA no se entera
 * solo de los cambios de pagina. Por eso `send_page_view` va apagado y cada
 * cambio de ruta se avisa a mano desde `SeguimientoDePaginas`. Dejar los dos
 * encendidos contaria dos veces la primera pagina.
 *
 * El backoffice no se mide: son tres personas trabajando, no visitas.
 */

const ID_MEDICION = (import.meta.env.VITE_GA_ID ?? '').trim();

type Gtag = (...argumentos: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

export function analiticaHabilitada(): boolean {
  return ID_MEDICION.length > 0;
}

/** Pide el script de GA una sola vez. No hace nada si no hay id de medicion. */
export function iniciarAnalitica(): void {
  if (!analiticaHabilitada() || window.gtag !== undefined) return;

  const capa: unknown[] = (window.dataLayer = window.dataLayer ?? []);
  // `gtag` exige el objeto `arguments`, no un arreglo: asi lo lee su libreria.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    capa.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', ID_MEDICION, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ID_MEDICION)}`;
  document.head.append(script);
}

/** Un evento cualquiera de GA4. No hace nada si GA no esta cargado. */
export function enviarEvento(nombre: string, parametros: Record<string, unknown>): void {
  if (!analiticaHabilitada() || window.gtag === undefined) return;
  window.gtag('event', nombre, parametros);
}

/** Una vista de pagina. La ruta es la del sitio, con su busqueda (`?termino=`). */
export function registrarVistaDePagina(ruta: string): void {
  if (ruta.startsWith('/admin')) return;

  enviarEvento('page_view', {
    page_path: ruta,
    page_location: window.location.href,
    page_title: document.title,
  });
}
